import { useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';

import greaseTrapIcon from '../../../../assets/icons/grease-trap.svg';
import saveBigIcon from '../../../../assets/icons/save.svg';
import { useModal } from '../../../../hooks/useModal';
import type { TGreaseTrapFilters } from '../../../../recoil/greaseTrapFilters';
import { greaseTrapFiltersState } from '../../../../recoil/greaseTrapFilters';
import { GET_GREASE_TRAP_OPTIONS } from '../../../../services/graphql/vendors/grease-traps/greaseTrapQuery';
import type { GetGreaseTrapOptionsResponse } from '../../../../types/greaseTrap';
import Breadcrumb from '../../../ui/common/Breadcrumb';
import { Button } from '../../../ui/common/Button';
import Dropdown from '../../../ui/common/Inputs/Dropdown';

const GREASE_TRAP_STATUS_OPTIONS = [
  { value: 'In Review', label: 'IN REVIEW' },
  { value: 'Pending Payment', label: 'PENDING PAYMENT' },
  { value: 'Paid', label: 'PAID' },
];

const FilterGreaseTraps = () => {
  const { t } = useTranslation('common');
  const [filters, setFilters] = useRecoilState(greaseTrapFiltersState);
  const { handleCloseAllModals } = useModal();

  const { setValue, watch, register, handleSubmit } =
    useForm<TGreaseTrapFilters>({
      defaultValues: {
        ...filters,
      },
    });

  const { data: greaseTrapOptions } = useQuery<GetGreaseTrapOptionsResponse>(
    GET_GREASE_TRAP_OPTIONS,
  );

  useEffect(() => {
    register('vendorId');
    register('vendorName');
    register('statusFilter');
  }, [register]);

  const closeOrder = () => {
    handleCloseAllModals();
  };

  const handleFiltersSave = (data: TGreaseTrapFilters) => {
    const selectedVendor =
      greaseTrapOptions?.getGreaseTrapOptions.vendorOptions.find(
        (option) => option.value === data.vendorId,
      );

    setFilters({
      ...filters,
      ...data,
      offset: 0,
      vendorId: data.vendorId || null,
      vendorName: selectedVendor?.label || null,
      statusFilter: data.statusFilter || null,
    });

    closeOrder();
  };

  return (
    <div>
      <Breadcrumb
        title="Grease Traps"
        secondTitle={t('modalFilterDropoffs.filters')}
        image={greaseTrapIcon}
      />
      <h1 className="text-xl font-[600]">Grease Traps</h1>
      <form
        onSubmit={handleSubmit(handleFiltersSave)}
        className="my-6 flex flex-col gap-2"
      >
        <div className="h-[60px]">
          <Dropdown
            label="Vendor"
            selectedValue={watch('vendorId') || undefined}
            options={
              greaseTrapOptions?.getGreaseTrapOptions.vendorOptions || []
            }
            onSelectChange={(value) => {
              const selectedVendor =
                greaseTrapOptions?.getGreaseTrapOptions.vendorOptions.find(
                  (option) => option.value === value,
                );

              setValue('vendorId', value);
              setValue('vendorName', selectedVendor?.label || null);
            }}
            isSearchable
          />
        </div>
        <div className="h-[60px]">
          <Dropdown
            label="Status"
            selectedValue={watch('statusFilter') || undefined}
            options={GREASE_TRAP_STATUS_OPTIONS}
            onSelectChange={(value) => setValue('statusFilter', value)}
          />
        </div>
        <div className="mt-2 flex justify-end">
          <Button
            text={t('modalFilterDropoffs.save')}
            variant="green"
            icon={saveBigIcon}
            type="submit"
            id="save-grease-trap-filters"
          />
        </div>
      </form>
    </div>
  );
};

export default FilterGreaseTraps;
